import { ParseBaseRepository } from '../../parse-base.repository';
import { type InvitationRepository, Invite } from '@telespot/domain';
import { InviteTopology, ParseInviteMapper } from './parse-invite.mapper';
import { OrganizationTopology } from '../organization';
import { ObjectTopology } from '../../parse.topology';

export class ParseInvitationRepository extends ParseBaseRepository implements InvitationRepository {
  private readonly inviteMapper = new ParseInviteMapper(this.parse);

  public async list(organizationId: string, limit: number, lastItemCreatedAt?: Date): Promise<Invite[]> {
    const Organization: typeof this.parse.Object = this.parse.Object.extend(OrganizationTopology.TABLE);
    const organization = Organization.createWithoutData(organizationId);
    const query = new this.parse.Query(InviteTopology.TABLE)
      .equalTo(InviteTopology.ORGANIZATION, organization)
      .descending(ObjectTopology.CREATED_AT)
      .limit(limit);

    if (lastItemCreatedAt) query.lessThan(ObjectTopology.CREATED_AT, lastItemCreatedAt);

    const results = await query.find();
    return results.map((result) => this.inviteMapper.toDomain(result));
  }

  public async delete(inviteId: string): Promise<void> {
    const ParseInvite = this.subclasses.getSubclass(InviteTopology.TABLE);

    const parseInvite = ParseInvite.createWithoutData(inviteId);

    await parseInvite.destroy(this.options);
  }

  public async getByCode(inviteCode: string): Promise<Invite> {
    const parseInvite = await new this.parse.Query(InviteTopology.TABLE)
      .equalTo(InviteTopology.CODE, inviteCode)
      .include(InviteTopology.ROLES)
      .first(this.options);

    return parseInvite ? this.inviteMapper.toDomain(parseInvite) : undefined;
  }

  public async save(invite: Invite): Promise<string> {
    const parseInvite = this.inviteMapper.fromDomain(invite);

    const { id } = await parseInvite.save(null, this.options);

    return id;
  }

  public async count(organizationId: string): Promise<number> {
    const Organization: typeof this.parse.Object = this.parse.Object.extend(OrganizationTopology.TABLE);
    const organization = Organization.createWithoutData(organizationId);
    const query = new this.parse.Query(InviteTopology.TABLE).equalTo(InviteTopology.ORGANIZATION, organization);
    return query.count();
  }
}
